import {
    getCurrentChainId,
    getLazpadApi,
    getServerApi,
    getTaskApi,
} from '@/gql';
import {
    ActivationCode,
    UserLazbubuInfoQuery,
} from '@/gql/graph/__generated__/request';
import { UserVo } from '@/gql/lazpad/__generated__/request';
import { getLazbubuDataConfig } from '@/utils';
import { autorun, computed, makeAutoObservable } from 'mobx';
import { ChainId, DefaultChainId, isBSCNetwork } from '../constant';

export class User {
    address: string = '';
    isDark: boolean = false;
    chainId: ChainId = getCurrentChainId();
    connected: boolean = false;
    userId: string | null = null;
    isLoadingUserDetail: boolean = false;

    userDetail: UserVo | null = null;
    referrerCode: string = ''; // Referrer's invite code that invited current user

    userLazbubuInfo?: UserLazbubuInfoQuery;
    userActivationCodes?: ActivationCode[];

    bscTotalScore: string = '0';

    constructor() {
        makeAutoObservable<this>(this);

        autorun(() => {
            if (this.userId && this.chainId && this.connected) {
                this.fetchUserDetail();
                this.getLazbubuData();
                this.fetchBscTotalScore();
                // this.fetchUserActivationCodes();
            }
        });

        autorun(() => {});
    }

    @computed get canOpenBlindBox() {
        return (
            !this.userLazbubuInfo?.lazbubuCreated &&
            !!this.userLazbubuInfo?.lazbubuData
        );
    }

    @computed get canOpenGiftBox() {
        return (
            !this.userLazbubuInfo?.giftLazbubuCreated &&
            !!this.userLazbubuInfo?.giftLazbubuData
        );
    }

    @computed get hasBlindBox() {
        return (
            !!this.userLazbubuInfo?.lazbubuCreated ||
            !!this.userLazbubuInfo?.lazbubuData
        );
    }

    @computed get hasGift() {
        return (
            !!this.userLazbubuInfo?.giftLazbubuCreated ||
            !!this.userLazbubuInfo?.giftLazbubuData
        );
    }

    @computed get lazbubuDataJson() {
        return getLazbubuDataConfig(this.userLazbubuInfo?.lazbubuData || '');
    }

    fetchUserDetail = async () => {
        if (!this.userId) return;

        try {
            this.isLoadingUserDetail = true;
            const api = getLazpadApi();
            const response = await api.getUserDetail({ id: this.userId });
            if (
                response?.getUserDetail?.data &&
                response?.getUserDetail?.success
            ) {
                this.userDetail = response.getUserDetail.data;
                return this.userDetail;
            }
        } catch (error) {
            console.error('Failed to fetch user detail:', error);
            this.userDetail = null;
            return null;
        } finally {
            this.isLoadingUserDetail = false;
        }
    };

    fetchUserActivationCodes = async () => {
        if (!this.userId) return;

        try {
            const api = getServerApi();
            const response = await api.getUserActivationCodes();
            if (
                response?.getUserActivationCodes &&
                response?.getUserActivationCodes
            ) {
                this.userActivationCodes = response.getUserActivationCodes;
                return this.userActivationCodes;
            }
        } catch (error) {
            console.error('Failed to fetch fetchUserActivationCodes:', error);
            this.userActivationCodes = [];
            return null;
        }
    };

    fetchBscTotalScore = async () => {
        if (!this.userId || !isBSCNetwork) return;

        try {
            const api = getTaskApi();
            const response = await api.getTotalScoreByTemplateIds({
                bizReq: {
                    userId: this.userId,
                    templateCodes: ['lazbubuAdventureB', 'lazbubuLevelUpB'],
                },
            });
            if (response?.getTotalScoreByTemplateIds?.data) {
                this.bscTotalScore = response.getTotalScoreByTemplateIds.data;
            }
        } catch (error) {
            console.error('Failed to fetch bsc total score:', error);
            this.bscTotalScore = '0';
        }
    };

    getLazbubuData = async () => {
        if (!this.address) return;

        try {
            this.isLoadingUserDetail = true;
            const api = getServerApi();
            const res = await api.userLazbubuInfo();
            if (res) {
                this.userLazbubuInfo = res;
                // this.userLazbubuInfo = {
                //     ...res,
                //     lazbubuCreated: undefined,
                //     lazbubuData:
                //         '0x1fdd0268847f5c2e917f3e219fab5ded3fc8ae1e1c37bf9e0daca327c0835bcd7fca1080b3c2ad028ed7d1b1702e5533d71cda8fc61aa12cc3d3989099c067d81b {"uuid":"a9078ea0-2b06-5c30-b4c6-e7a8a66e79d5","creatorAddress":"0x1838be1ce335f3658072a1db6ad3a6b04629bf5c","rarity":0,"image":"0-17.png"}',
                // };
                return this.userLazbubuInfo;
            }
        } catch (error) {
            this.userLazbubuInfo = undefined;
            console.error('Failed to fetch user detail:', error);
            return null;
        }
    };

    toggleTheme = () => {
        this.isDark = !this.isDark;
    };

    setConnected = (connected: boolean) => {
        this.connected = connected;
    };

    setChainId = (chainId: ChainId) => {
        this.chainId = chainId;
    };

    setAddress = (address: string) => {
        this.address = address;
    };

    setReferrerCode = (referrerCode: string) => {
        this.referrerCode = referrerCode;
    };

    setUserId = (userId: string | null) => {
        this.userId = userId;
    };

    clearLoginInfo = () => {
        console.log('clearLoginInfo');
        this.chainId = DefaultChainId;
        this.address = '';
        this.connected = false;
        this.userId = null;
        this.isLoadingUserDetail = false;
        this.userDetail = null;
        this.userLazbubuInfo = undefined;
        this.userActivationCodes = [];
    };
}

export default new User();
